/**
 * Who works here, and what they are allowed to do.
 *
 * WHY THIS SCREEN EXISTS
 * ----------------------
 * Until it did, adding a member of staff meant a developer editing the
 * database. What happens instead is everybody sharing one login — and then
 * nothing in the audit trail means anything, because every action was taken
 * by "the manager".
 *
 * The rules here are the same ones the server enforces, worked out on screen
 * so nobody is offered a button that will refuse them.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { useAuth } from '../../stores/auth.js';
import { formatServerTime, formatPKR } from '@foodrun/shared';
import {
  accountState,
  canArchive,
  canDeactivate,
  canSettleAccounts,
  readableRole,
} from './staff.js';
import { DataTable } from '../../components/ui/DataTable.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { HireSheet } from './HireSheet.jsx';
import { AccountHolderSheet } from './AccountHolderSheet.jsx';
import { PersonSheet } from './PersonSheet.jsx';

export default function StaffPage() {
  const client = useQueryClient();
  const me = useAuth((s) => s.user);
  const can = useAuth((s) => s.can);

  const [hiring, setHiring] = useState(false);
  const [looking, setLooking] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const staff = useQuery({
    queryKey: ['staff', showArchived ? 'archived' : 'active'],
    queryFn: () => api.get(showArchived ? '/admin/staff?archived=only' : '/admin/staff'),
  });

  const roles = useQuery({
    queryKey: ['staff', 'roles'],
    queryFn: () => api.get('/admin/roles'),
    staleTime: 10 * 60_000,
  });

  const refresh = () => client.invalidateQueries({ queryKey: ['staff'] });
  const rows = staff.data?.staff ?? [];
  // Staff on one list; the people who only have a tab here on another.
  const staffRows = rows.filter((r) => !Number(r.account_only));
  const holderRows = rows.filter((r) => Number(r.account_only));
  const [addingHolder, setAddingHolder] = useState(false);

  return (
    <div className="mx-auto max-w-5xl p-4">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">
            {showArchived ? 'Removed staff' : 'Staff'}
          </h1>
          <p className="mt-0.5 text-sm text-ink-2">
            {showArchived
              ? 'Accounts taken off the list. Their orders and shifts were kept — restore anyone to bring them back.'
              : 'Everyone with an account, and what their account can do.'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* A quiet way in and out of the removed list — off to the side of the
              day-to-day list, since it is rarely what anyone came here for. */}
          <button
            onClick={() => setShowArchived((v) => !v)}
            className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-ink-2 hover:bg-surface-3"
          >
            {showArchived ? '← Back to staff' : 'Removed staff'}
          </button>

          {!showArchived && can('auth.create.auth') && (
            <>
              <Button variant="secondary" size="lg" onClick={() => setAddingHolder(true)}>
                Add an account holder
              </Button>
              <Button size="lg" onClick={() => setHiring(true)}>
                Add someone
              </Button>
            </>
          )}
        </div>
      </div>

      <DataTable
        rows={staffRows}
        loading={staff.isLoading}
        error={staff.isError}
        searchable
        searchKeys={['first_name', 'last_name', 'email']}
        empty={showArchived ? 'Nobody has been removed.' : 'Nobody has an account yet.'}
        columns={[
          {
            key: 'first_name',
            label: 'Name',
            render: (row) => `${row.first_name} ${row.last_name}`.trim(),
            tone: () => 'text-ink',
          },
          { key: 'email', label: 'Email' },
          {
            key: 'roles',
            label: 'Can do',
            render: (row) => (row.roles ?? []).map(readableRole).join(', ') || '—',
          },
          {
            key: 'last_login',
            label: 'Last signed in',
            // Somebody who has never signed in is usually somebody whose
            // password never reached them.
            render: (row) =>
              row.last_login ? formatServerTime(row.last_login, { year: undefined }) : 'Never',
            tone: (row) => (row.last_login ? 'text-ink-2' : 'text-ink-3'),
          },
          {
            key: 'is_active',
            label: 'Account',
            render: (row) => {
              const state = accountState(row);
              return (
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${state.tone}`}>
                  {state.label}
                </span>
              );
            },
          },
          {
            // What this person has put on their account and not yet cleared.
            key: 'account_balance',
            label: 'Owes',
            align: 'right',
            render: (row) => (Number(row.account_balance) > 0 ? formatPKR(row.account_balance) : '—'),
            tone: (row) => (Number(row.account_balance) > 0 ? 'text-warn' : 'text-ink-3'),
          },
          {
            key: 'id',
            label: '',
            align: 'right',
            render: (row) => (
              <button
                onClick={() => setLooking(row)}
                className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-brand-300 hover:bg-brand-400/15"
              >
                {can('auth.update.auth') ? 'Manage' : 'Details'}
              </button>
            ),
          },
        ]}
      />

      {/* People who are not staff but run a tab: a name, a number, what they
          owe. No login, no roles. */}
      {!showArchived && (holderRows.length > 0 || can('auth.create.auth')) && (
        <section className="mt-8">
          <h2 className="font-semibold text-lg text-ink">Account holders</h2>
          <p className="mt-0.5 text-sm text-ink-2">Not staff, but bills can be put on their account at the till and cleared here. They cannot sign in.</p>
          <div className="mt-3">
            <DataTable
              rows={holderRows}
              loading={staff.isLoading}
              error={staff.isError}
              empty="Nobody has an account yet. Add an account holder for a regular who settles later."
              columns={[
                { key: 'first_name', label: 'Name', render: (row) => `${row.first_name} ${row.last_name}`.trim(), tone: () => 'text-ink' },
                { key: 'phone', label: 'Phone', render: (row) => row.phone || '—', tone: (row) => (row.phone ? 'text-ink-2' : 'text-ink-3') },
                {
                  key: 'account_balance',
                  label: 'Owes',
                  align: 'right',
                  render: (row) => (Number(row.account_balance) > 0 ? formatPKR(row.account_balance) : '—'),
                  tone: (row) => (Number(row.account_balance) > 0 ? 'text-warn' : 'text-ink-3'),
                },
                {
                  key: 'id',
                  label: '',
                  align: 'right',
                  render: (row) => (
                    <button onClick={() => setLooking(row)} className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-brand-300 hover:bg-brand-400/15">
                      {can('auth.update.auth') ? 'Manage' : 'Details'}
                    </button>
                  ),
                },
              ]}
            />
          </div>
        </section>
      )}

      {addingHolder && (
        <AccountHolderSheet
          onClose={() => setAddingHolder(false)}
          onDone={() => {
            setAddingHolder(false);
            refresh();
          }}
        />
      )}

      {hiring && (
        <HireSheet
          roles={roles.data?.roles ?? []}
          onClose={() => setHiring(false)}
          onHired={() => {
            setHiring(false);
            refresh();
          }}
        />
      )}

      {looking && (
        <PersonSheet
          person={looking}
          roles={roles.data?.roles ?? []}
          me={me}
          readOnly={!can('auth.update.auth')}
          canDeactivate={canDeactivate(me, looking)}
          canArchive={canArchive(me, looking)}
          canSettle={canSettleAccounts(me)}
          onClose={() => setLooking(null)}
          onChanged={() => {
            setLooking(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
